import { createFileRoute, redirect } from '@tanstack/react-router'
import { findCachedScene, scene } from '@/lib/queries/scenes'
import { decodeId, encodeId } from '@/lib/short-uuid'
import { SceneDetailPage } from '@/features/workspaces/scenes/detail'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/workspace/$workspaceId/scenes/$sceneId'
)({
  params: {
    parse: (rawParams: { sceneId: string }) => {
      try {
        return { sceneId: decodeId(rawParams.sceneId) }
      } catch {
        return { sceneId: rawParams.sceneId }
      }
    },
    stringify: (params: { sceneId: string }) => ({
      sceneId: encodeId(params.sceneId),
    }),
  },
  loader: async ({ context, params }) => {
    const { queryClient } = context

    try {
      // Check infinite cache first (for optimistic updates)
      const cachedData = findCachedScene(
        queryClient,
        params.workspaceId,
        params.sceneId
      )

      // ALWAYS ensure the query is properly initialized with its queryFn
      // This is critical for invalidateQueries to work later
      const queryOptions = scene.findOne(params.sceneId)

      // Only seed if no data exists yet (don't overwrite fresher data from realtime refetch)
      if (cachedData && !queryClient.getQueryData(queryOptions.queryKey)) {
        queryClient.setQueryData(queryOptions.queryKey, cachedData)
      }

      const data = await queryClient.query({
        ...queryOptions,
        staleTime: 'static',
      })

      if (!data || data.workspace_id !== params.workspaceId) {
        throw new Error('Invalid scene')
      }
    } catch {
      throw redirect({
        to: '/org/$orgId/workspace/$workspaceId/scenes',
        params: { orgId: params.orgId, workspaceId: params.workspaceId },
        replace: true,
      })
    }
  },
  component: SceneDetailPage,
})
